import { SafeError, SafePromise, safeAwait } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { DesktopNotificationMode, DesktopNotificationPermissionStatus, isDesktopNotificationMode } from './entities';
import { Session } from '@benzinga/session';
import { DesktopNotificationEnvironment } from './environment';

interface DesktopNotificationError {
  error: SafeError;
  type: 'error';
  typeError: 'notification_dispatch_error';
}

interface DesktopNotificationMessage {
  title: string;
  options: NotificationOptions;
  onClick?: () => void;
  type: 'utils-notification:notification';
}

export type DesktopNotificationManagerEvent = DesktopNotificationError | DesktopNotificationMessage;

export const NotificationModeStorageKey = 'notificationMode' as const;

export class DesktopNotificationManager extends Subscribable<DesktopNotificationManagerEvent> {
  private notificationMode: DesktopNotificationMode;

  private defaultNotificationOptions: NotificationOptions;

  constructor(session: Session) {
    super();
    this.defaultNotificationOptions = {
      icon: session.getEnvironment(DesktopNotificationEnvironment).notificationIcon,
      lang: 'EN',
    };

    const mode = window.localStorage.getItem(NotificationModeStorageKey);
    this.notificationMode = isDesktopNotificationMode(mode) ? mode : 'off';
  }

  public static getName = () => 'benzinga-utils-notification';

  public static allowedDesktopNotifications = () => this.getDesktopNotificationsPermission() === 'granted';

  public static getDesktopNotificationsPermission = (): DesktopNotificationPermissionStatus => {
    try {
      return window.Notification.permission; // TODO: this is a typescript bug, readonly permission does not exist on global object
    } catch (_error: any) {
      return 'unsupported';
    }
  };

  public requestDesktopNotification = async (): SafePromise<NotificationPermission | Error> => {
    if (typeof window.Notification === 'function') {
      return safeAwait(window.Notification.requestPermission());
    }
    return Promise.reject(new Error('Notifications are not supported in this browser'));
  };

  public dispatchNotification = async (title: string, options: NotificationOptions = {}, onClick?: () => void) => {
    if (this.showNotification(title, options, onClick) === false) {
      this.setNotificationsMode('off');
      const permission = await this.requestDesktopNotification();
      if (permission.ok === 'granted') {
        this.setNotificationsMode('native');
      }

      this.dispatch({
        error: new SafeError(
          'You have blocked notifications for Benzinga Pro in your browser, or are using an unsupported browser. Enable notifications to use this feature. Contact support if you have any trouble 1 (877) 440-9464.',
          'notification_dispatch_error',
        ),
        type: 'error',
        typeError: 'notification_dispatch_error',
      });
    }
  };

  public notificationsMode = () => this.notificationMode;

  public setNotificationsMode = async (mode: DesktopNotificationMode): Promise<DesktopNotificationPermissionStatus> => {
    switch (mode) {
      case 'native':
        return this.newRequestDesktopNotificationPermission().then(status => {
          if (status === 'granted') {
            this.notificationMode = 'native';
          } else {
            this.notificationMode = 'internal';
          }
          window.localStorage.setItem(NotificationModeStorageKey, this.notificationMode);
          return status;
        });
      case 'internal':
        this.notificationMode = 'internal';
        window.localStorage.setItem(NotificationModeStorageKey, this.notificationMode);
        return 'granted';
      case 'off':
        this.notificationMode = 'off';
        window.localStorage.setItem(NotificationModeStorageKey, this.notificationMode);
        return 'granted';
    }
  };

  public showInternalNotification = (title: string, options: NotificationOptions = {}, onClick?: () => void) => {
    this.dispatch({
      onClick,
      options,
      title,
      type: 'utils-notification:notification',
    });
    return true;
  };

  private showNotification = (title: string, options: NotificationOptions = {}, onClick?: () => void) => {
    options = {
      ...this.defaultNotificationOptions,
      ...options,
    };

    switch (this.notificationMode) {
      case 'native':
        return this.showDesktopNotification(title, options, onClick);
      case 'internal':
        return this.showInternalNotification(title, options, onClick);
      case 'off':
        return true;
    }
  };

  private newRequestDesktopNotificationPermission = async (): Promise<DesktopNotificationPermissionStatus> => {
    if (typeof window.Notification === 'function') {
      const maybePromise = await window.Notification.requestPermission();
      return maybePromise;
    }
    return 'unsupported';
  };

  private showDesktopNotification = (title: string, options: NotificationOptions = {}, onClick?: () => void) => {
    if (DesktopNotificationManager.allowedDesktopNotifications()) {
      const notification = new window.Notification(title, options);

      // on click, we want to focus the window again.
      notification.addEventListener('click', () => {
        parent.focus(); // webkit
        window.focus();
        onClick?.();
        // TODO: typescript doesn't like this, double check notification.close() is equivalent
        // event.target.close();
        notification.close();
      });

      return true;
    } else {
      this.requestDesktopNotification();
      return false;
    }
  };
}
